import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle, KeyRound } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const ForgotPasswordPage = () => {
  const [step, setStep] = useState('request'); // 'request', 'verify', 'success'
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('Reset instructions sent to your email');
      
      // For testing: if token is returned, show it
      if (response.data.reset_token) {
        setToken(response.data.reset_token);
      }
      setStep('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      toast.success('Password reset successfully!');
      setStep('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-aura-bg flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <Link to="/login" className="flex items-center gap-2 text-aura-text-muted hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back to Login</span>
        </Link>
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-aura-primary rounded-sm flex items-center justify-center">
            <span className="font-heading font-black text-white text-lg">A</span>
          </div>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          
          {/* Step 1: Request Reset */}
          {step === 'request' && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-aura-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-aura-primary" />
                </div>
                <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight mb-2">
                  Forgot Password?
                </h1>
                <p className="text-aura-text-muted">
                  Enter your email and we'll send you reset instructions
                </p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="athlete@example.com"
                    required
                    className="h-12 bg-aura-surface border-white/10 text-white placeholder:text-aura-text-subtle focus:border-aura-primary"
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider"
                  data-testid="send-reset-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Step 2: Enter Token & New Password */}
          {step === 'verify' && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-aura-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-8 h-8 text-aura-secondary" />
                </div>
                <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight mb-2">
                  Reset Password
                </h1>
                <p className="text-aura-text-muted">
                  Enter the reset token from your email and your new password
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="token" className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Reset Token
                  </Label>
                  <Input
                    id="token"
                    type="text"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Paste token from email"
                    required
                    className="h-12 bg-aura-surface border-white/10 text-white placeholder:text-aura-text-subtle focus:border-aura-primary font-mono text-sm"
                    data-testid="reset-token-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    New Password
                  </Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                    className="h-12 bg-aura-surface border-white/10 text-white placeholder:text-aura-text-subtle focus:border-aura-primary"
                    data-testid="new-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    required
                    className="h-12 bg-aura-surface border-white/10 text-white placeholder:text-aura-text-subtle focus:border-aura-primary"
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider"
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Reset Password'
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => setStep('request')}
                  className="w-full text-center text-sm text-aura-text-muted hover:text-white transition-colors"
                >
                  Didn't receive the email? Try again
                </button>
              </form>
            </>
          )}

          {/* Step 3: Success */}
          {step === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight mb-2">
                Password Reset!
              </h1>
              <p className="text-aura-text-muted mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-12 bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default ForgotPasswordPage;
